import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { EntityCombobox } from '@/components/shared/entity-combobox';
import { PageHeader } from '@/components/shared/page-header';
import { Tip } from '@/components/shared/tip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import type { PrayerDetail } from '@/pages/prayer/types';

interface MemberOption {
  id: string;
  firstName: string;
  lastName: string;
}

export const PrayerRequestDetailPage = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const { t } = useTranslation('prayer');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [memberQuery, setMemberQuery] = useState('');
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [responseText, setResponseText] = useState('');
  const [archiveOpen, setArchiveOpen] = useState(false);

  const detail = useQuery({
    queryKey: ['prayer', 'requests', requestId],
    enabled: Boolean(requestId),
    queryFn: () => api.get<PrayerDetail>(`/prayer/requests/${requestId}`),
  });

  const members = useQuery({
    queryKey: ['people', 'members', 'picker', memberQuery],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '20' });
      if (memberQuery.trim()) params.set('q', memberQuery.trim());
      return api.get<{ data: MemberOption[] }>(`/people/members?${params.toString()}`);
    },
  });

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? []).map((member) => ({
        id: member.id,
        label: `${member.firstName} ${member.lastName}`.trim(),
      })),
    [members.data?.data],
  );

  const selectedMember = useMemo(() => {
    const currentId = assigneeId ?? detail.data?.assignedToMemberId ?? null;
    if (!currentId) return null;
    return memberOptions.find((option) => option.id === currentId) ?? null;
  }, [assigneeId, detail.data?.assignedToMemberId, memberOptions]);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['prayer', 'requests'] });
    void queryClient.invalidateQueries({ queryKey: ['prayer', 'requests', requestId] });
  };

  const onError = (err: unknown) => {
    toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
  };

  const assign = useMutation({
    mutationFn: () =>
      api.patch<PrayerDetail>(`/prayer/requests/${requestId}/assign`, {
        assignedToMemberId: assigneeId,
      }),
    onSuccess: () => {
      toast.success(t('assign.saved'));
      invalidate();
    },
    onError,
  });

  const respond = useMutation({
    mutationFn: () =>
      api.post<PrayerDetail>(`/prayer/requests/${requestId}/respond`, {
        message: responseText,
      }),
    onSuccess: () => {
      toast.success(t('respond.saved'));
      setResponseText('');
      invalidate();
    },
    onError,
  });

  const markPraying = useMutation({
    mutationFn: () =>
      api.patch<PrayerDetail>(`/prayer/requests/${requestId}/status`, { status: 'praying' }),
    onSuccess: invalidate,
    onError,
  });

  const markAnswered = useMutation({
    mutationFn: () =>
      api.patch<PrayerDetail>(`/prayer/requests/${requestId}/status`, { status: 'answered' }),
    onSuccess: invalidate,
    onError,
  });

  const archive = useMutation({
    mutationFn: () => api.patch<PrayerDetail>(`/prayer/requests/${requestId}/archive`, {}),
    onSuccess: () => {
      toast.success(t('actions.archived'));
      setArchiveOpen(false);
      invalidate();
    },
    onError,
  });

  const row = detail.data;
  const canAssign = row && row.status !== 'archived' && row.status !== 'answered';
  const canRespond =
    row &&
    (row.status === 'assigned' || row.status === 'praying') &&
    row.assignedToMemberId !== null;

  return (
    <div>
      <PageHeader
        title={t('detail.title')}
        tipId="prayer.whoCanRead"
        breadcrumbs={[
          { label: t('title'), to: '/prayer' },
          { label: row ? t(`category.${row.category}`) : t('detail.title') },
        ]}
      />

      {detail.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
        </div>
      ) : null}

      {detail.isError ? (
        <p className="text-body-sm text-danger">{tCommon('state.errorBody')}</p>
      ) : null}

      {row ? (
        <div className="space-y-6">
          <Tip tipId="prayer.openAudit" className="mb-2" />
          <div className="flex flex-wrap items-center gap-2">
            <Badge>{t(`category.${row.category}`)}</Badge>
            <Badge variant="neutral">{t(`status.${row.status}`)}</Badge>
            {row.isAnonymous ? (
              <span className="text-body-sm text-fg-secondary">{t('detail.anonymous')}</span>
            ) : null}
          </div>
          {row.isAnonymous ? <Tip tipId="prayer.anonymous" /> : null}

          <section className="rounded-lg border border-border p-4">
            <h2 className="mb-2 text-body font-medium">{t('detail.request')}</h2>
            <p className="whitespace-pre-wrap text-body-sm">{row.body}</p>
          </section>

          {row.response ? (
            <section className="rounded-lg border border-border p-4">
              <h2 className="mb-2 text-body font-medium">{t('detail.response')}</h2>
              <p className="whitespace-pre-wrap text-body-sm">{row.response}</p>
            </section>
          ) : (
            <p className="text-body-sm text-fg-secondary">{t('detail.noResponse')}</p>
          )}

          {canAssign ? (
            <section className="rounded-lg border border-border p-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>{t('assign.label')}</Label>
                  <EntityCombobox
                    value={assigneeId ?? row.assignedToMemberId}
                    onChange={setAssigneeId}
                    options={memberOptions}
                    selected={selectedMember}
                    onQueryChange={setMemberQuery}
                    loading={members.isFetching}
                    placeholder={t('assign.placeholder')}
                    clearLabel={tCommon('action.clearSelection')}
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    loading={assign.isPending}
                    disabled={!(assigneeId ?? row.assignedToMemberId)}
                    onClick={() => assign.mutate()}
                  >
                    {t('assign.action')}
                  </Button>
                </div>
              </div>
            </section>
          ) : null}

          {canRespond ? (
            <section className="rounded-lg border border-border p-4">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1.5">
                  <Label>{t('respond.label')}</Label>
                  <Textarea
                    value={responseText}
                    onChange={(event) => setResponseText(event.target.value)}
                    placeholder={t('respond.placeholder')}
                    rows={4}
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    loading={respond.isPending}
                    disabled={!responseText.trim()}
                    onClick={() => respond.mutate()}
                  >
                    {t('respond.action')}
                  </Button>
                </div>
              </div>
            </section>
          ) : null}

          {row.status !== 'archived' ? (
            <div className="flex flex-wrap justify-end gap-2">
              {row.status === 'assigned' ? (
                <Button variant="outline" loading={markPraying.isPending} onClick={() => markPraying.mutate()}>
                  {t('actions.markPraying')}
                </Button>
              ) : null}
              {row.status === 'assigned' || row.status === 'praying' ? (
                <Button
                  variant="outline"
                  loading={markAnswered.isPending}
                  onClick={() => markAnswered.mutate()}
                >
                  {t('actions.markAnswered')}
                </Button>
              ) : null}
              <Button variant="destructive" onClick={() => setArchiveOpen(true)}>
                {t('actions.archive')}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        title={t('confirm.archive.title')}
        description={t('confirm.archive.description')}
        confirmLabel={t('confirm.archive.confirm')}
        cancelLabel={tCommon('action.cancel')}
        destructive
        loading={archive.isPending}
        onConfirm={() => archive.mutate()}
      />
    </div>
  );
};
